/**
 * Per-chart analysis helpers. Each `analyseXxx()` takes the same raw data
 * the chart renders and returns up to 3–4 short bullet points with a
 * severity (ok / info / warn / bad). The component <ChartAnalysis /> in
 * Charts.tsx renders these as a coloured block below the chart.
 *
 * Thresholds are intentionally project-specific (e.g. KV norm 17–22 for
 * this station). Tweak as you learn more about the equipment.
 */
import { kvOf, type Measurement, type Period, type Turbine, type TurbineEvent } from "../types";

export type Severity = "ok" | "info" | "warn" | "bad";
export interface AnalysisItem { severity: Severity; text: string; }

type T = (key: string, vars?: Record<string, string | number>) => string;

// ---------- helpers ----------

function num(v: number): string {
  return Number.isInteger(v) ? v.toString() : v.toFixed(1);
}

function avg(arr: number[]): number {
  if (arr.length === 0) return 0;
  return arr.reduce((a, b) => a + b, 0) / arr.length;
}

function stddev(arr: number[]): number {
  if (arr.length < 2) return 0;
  const m = avg(arr);
  return Math.sqrt(arr.reduce((a, b) => a + (b - m) ** 2, 0) / arr.length);
}

function fmtHm(sec: number, t: T): string {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h <= 0) return `${m} ${t("dashboard.turbines.unit_min")}`;
  if (m === 0) return `${h} ${t("dashboard.turbines.unit_h")}`;
  return `${h} ${t("dashboard.turbines.unit_h")} ${m} ${t("dashboard.turbines.unit_min")}`;
}

function periodWindowMs(p: Period): { start: number; end: number } {
  const today = new Date();
  const ymd = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const minus = (n: number) => { const d = new Date(today); d.setDate(d.getDate() - n); return d; };
  let start: Date, end: Date;
  switch (p) {
    case "today": start = ymd(today); end = new Date(start.getTime() + 86400_000); break;
    case "yest":  end = ymd(today); start = new Date(end.getTime() - 86400_000); break;
    case "7d":    end = new Date(ymd(today).getTime() + 86400_000); start = new Date(end.getTime() - 7 * 86400_000); break;
    case "30d":   end = new Date(ymd(today).getTime() + 86400_000); start = new Date(end.getTime() - 30 * 86400_000); break;
    case "all":   start = new Date(2020, 0, 1); end = new Date(ymd(today).getTime() + 2 * 86400_000); break;
  }
  return { start: start.getTime(), end: end.getTime() };
}


/* ---------- per-chart analyses ---------- */

export function analyseKwKv(items: Measurement[], t: T): AnalysisItem[] {
  const online = items.filter(m => !m.is_offline && (m.kw !== null || kvOf(m) !== null));
  if (online.length === 0) return [{ severity: "info", text: t("analysis.no_data") }];

  const kvVals = online.map(m => kvOf(m)).filter((v): v is number => v !== null);
  const kwVals = online.map(m => m.kw).filter((v): v is number => v !== null);
  const out: AnalysisItem[] = [];

  if (kvVals.length > 0) {
    const kvMin = Math.min(...kvVals), kvMax = Math.max(...kvVals);
    if (kvMax > 22 || kvMin < 17) {
      out.push({ severity: "warn", text: t("analysis.kv_out", { min: num(kvMin), max: num(kvMax) }) });
    } else {
      out.push({ severity: "ok", text: t("analysis.kv_ok", { min: num(kvMin), max: num(kvMax) }) });
    }
  }
  if (kwVals.length > 1) {
    const m = avg(kwVals);
    const cv = m > 0 ? stddev(kwVals) / m : 0;
    if (cv > 0.30) {
      out.push({ severity: "warn", text: t("analysis.kw_unstable", { cv: Math.round(cv * 100), avg: Math.round(m) }) });
    } else {
      out.push({ severity: "ok", text: t("analysis.kw_stable", { avg: Math.round(m) }) });
    }
  }
  out.push({ severity: "info", text: t("analysis.records", { n: online.length }) });
  return out;
}

export function analyseCompare(items: Measurement[], turbines: Turbine[], t: T): AnalysisItem[] {
  const out: AnalysisItem[] = [];
  const stats: { name: string; n: number; avg: number }[] = [];
  for (const tb of turbines) {
    const vals = items
      .filter(m => m.turbine_id === tb.id && !m.is_offline && m.kw !== null)
      .map(m => m.kw as number);
    stats.push({ name: tb.name, n: vals.length, avg: vals.length ? avg(vals) : 0 });
  }
  const active = stats.filter(s => s.n > 0);
  const idle = stats.filter(s => s.n === 0);
  if (active.length === 0) return [{ severity: "info", text: t("analysis.no_data") }];

  for (const s of idle) {
    out.push({ severity: "info", text: t("analysis.turbine_idle", { name: s.name }) });
  }
  if (active.length >= 2) {
    const sorted = [...active].sort((a, b) => b.avg - a.avg);
    const hi = sorted[0], lo = sorted[sorted.length - 1];
    const ratio = lo.avg > 0 ? hi.avg / lo.avg : 0;
    if (lo.avg > 0 && ratio > 1.5) {
      out.push({ severity: "warn", text: t("analysis.compare_imbalance", {
        hi: hi.name, lo: lo.name, hiAvg: Math.round(hi.avg), loAvg: Math.round(lo.avg),
      }) });
    } else {
      out.push({ severity: "ok", text: t("analysis.compare_balanced") });
    }
  }
  for (const s of active) {
    out.push({ severity: "info", text: t("analysis.turbine_avg", { name: s.name, avg: Math.round(s.avg) }) });
  }
  return out;
}

export function analyseTimeline(events: TurbineEvent[], turbines: Turbine[], period: Period, t: T): AnalysisItem[] {
  const { start, end } = periodWindowMs(period);
  const endEff = Math.min(end, Date.now());
  const totalMs = endEff - start;
  if (totalMs <= 0 || turbines.length === 0) return [{ severity: "info", text: t("analysis.no_data") }];

  const out: AnalysisItem[] = [];
  let totalUp = 0, totalKnown = 0, totalStops = 0;

  for (const tb of turbines) {
    const evs = events.filter(e => e.turbine_id === tb.id);
    if (evs.length === 0) continue;
    const firstEv = new Date(evs[0].event_time).getTime();
    const effStart = Math.max(start, firstEv);
    if (endEff <= effStart) continue;

    const before = evs.filter(e => new Date(e.event_time).getTime() < effStart);
    let running = before.length > 0 && before[before.length - 1].event_type === "start";
    let cursor = effStart;
    let up = 0, down = 0, stops = 0;
    for (const e of evs.filter(x => {
      const t0 = new Date(x.event_time).getTime();
      return t0 >= effStart && t0 < endEff;
    })) {
      const at = new Date(e.event_time).getTime();
      if (at > cursor) {
        if (running) up += (at - cursor); else down += (at - cursor);
      }
      if (e.event_type === "stop") stops += 1;
      running = e.event_type === "start";
      cursor = at;
    }
    if (cursor < endEff) {
      if (running) up += (endEff - cursor); else down += (endEff - cursor);
    }
    const known = up + down;
    if (known === 0) continue;
    totalUp += up; totalKnown += known; totalStops += stops;
    const pct = Math.round(up / known * 100);
    let sev: Severity = "ok";
    if (pct < 70) sev = "bad"; else if (pct < 90) sev = "warn";
    out.push({ severity: sev, text: t("analysis.timeline_turbine", {
      name: tb.name, uptime: pct, downtime: fmtHm(Math.round(down / 1000), t),
    }) });
  }

  if (totalKnown > 0) {
    const overallPct = Math.round(totalUp / totalKnown * 100);
    let sev: Severity = "ok";
    if (overallPct < 70) sev = "bad"; else if (overallPct < 90) sev = "warn";
    out.unshift({ severity: sev, text: t("analysis.timeline_overall", {
      uptime: overallPct, stops: totalStops,
    }) });
  } else {
    return [{ severity: "info", text: t("analysis.no_data") }];
  }
  return out;
}

export function analyseTemperatures(items: Measurement[], t: T): AnalysisItem[] {
  const fields = ["bt_371", "bt_372", "bt_331", "bt_332"];
  const all: number[] = [];
  const maxByField: Record<string, number> = {};
  for (const m of items) {
    if (m.is_offline) continue;
    for (const f of fields) {
      const v = (m as unknown as Record<string, number | null>)[f];
      if (v !== null && v !== undefined) {
        all.push(v);
        if (!(f in maxByField) || v > maxByField[f]) maxByField[f] = v;
      }
    }
  }
  if (all.length === 0) return [{ severity: "info", text: t("analysis.no_data") }];

  const out: AnalysisItem[] = [];
  const globalMax = Math.max(...all);
  if (globalMax > 85) {
    const hot = Object.entries(maxByField).filter(([_, v]) => v > 85).map(([k]) => k.toUpperCase().replace("_", " "));
    out.push({ severity: "bad", text: t("analysis.temp_critical", { max: num(globalMax), sensors: hot.join(", ") }) });
  } else if (globalMax > 70) {
    out.push({ severity: "warn", text: t("analysis.temp_warn", { max: num(globalMax) }) });
  } else {
    out.push({ severity: "ok", text: t("analysis.temp_ok", { max: num(globalMax) }) });
  }
  out.push({ severity: "info", text: t("analysis.temp_avg", { avg: num(avg(all)) }) });
  return out;
}

export function analyseFrequency(items: Measurement[], t: T): AnalysisItem[] {
  const vals = items.filter(m => !m.is_offline && m.frequency !== null).map(m => m.frequency as number);
  if (vals.length === 0) return [{ severity: "info", text: t("analysis.no_data") }];
  const low = vals.filter(v => v < 49.5).length;
  const high = vals.filter(v => v > 50.5).length;
  const out: AnalysisItem[] = [];
  if (low + high === 0) {
    out.push({ severity: "ok", text: t("analysis.freq_ok", {
      min: vals.reduce((a, b) => Math.min(a, b)).toFixed(2),
      max: vals.reduce((a, b) => Math.max(a, b)).toFixed(2),
    }) });
  } else {
    out.push({ severity: "bad", text: t("analysis.freq_bad", {
      low, high, total: vals.length,
    }) });
  }
  out.push({ severity: "info", text: t("analysis.freq_avg", { avg: avg(vals).toFixed(2) }) });
  return out;
}

export function analyseStopReasons(events: TurbineEvent[], t: T): AnalysisItem[] {
  const stops = events.filter(e => e.event_type === "stop");
  if (stops.length === 0) return [{ severity: "ok", text: t("analysis.stop_none") }];
  const counts = new Map<string, number>();
  for (const e of stops) {
    const r = (e.reason || "").trim() || t("charts.reason_unknown");
    counts.set(r, (counts.get(r) ?? 0) + 1);
  }
  const sorted = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
  const [topName, topCount] = sorted[0];
  const topPct = Math.round(topCount / stops.length * 100);
  const out: AnalysisItem[] = [];
  out.push({ severity: "info", text: t("analysis.stop_total", { n: stops.length }) });
  if (topPct >= 50) {
    out.push({ severity: "warn", text: t("analysis.stop_dominant", { name: topName, pct: topPct }) });
  } else {
    out.push({ severity: "info", text: t("analysis.stop_top", { name: topName, pct: topPct }) });
  }
  if (sorted.length >= 3) {
    out.push({ severity: "info", text: t("analysis.stop_variety", { n: sorted.length }) });
  }
  return out;
}

export function analyseKwHistogram(items: Measurement[], t: T): AnalysisItem[] {
  const vals = items.filter(m => !m.is_offline && m.kw !== null).map(m => m.kw as number);
  if (vals.length === 0) return [{ severity: "info", text: t("analysis.no_data") }];
  const max = Math.max(...vals);
  const bucketSize = 200;
  const counts = new Map<string, number>();
  for (const v of vals) {
    const idx = Math.floor(v / bucketSize);
    const key = `${idx * bucketSize}-${(idx + 1) * bucketSize}`;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const sorted = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
  const [topRange, topCount] = sorted[0];
  const topPct = Math.round(topCount / vals.length * 100);
  const lowCount = vals.filter(v => v < max * 0.3).length;
  const lowPct = Math.round(lowCount / vals.length * 100);
  const out: AnalysisItem[] = [];
  out.push({ severity: "info", text: t("analysis.hist_top", { range: topRange, pct: topPct }) });
  if (lowPct > 30) {
    out.push({ severity: "warn", text: t("analysis.hist_low", { pct: lowPct }) });
  } else {
    out.push({ severity: "ok", text: t("analysis.hist_normal", { pct: 100 - lowPct }) });
  }
  out.push({ severity: "info", text: t("analysis.hist_avg", { avg: Math.round(avg(vals)) }) });
  return out;
}

export function analyseCosKvar(items: Measurement[], t: T): AnalysisItem[] {
  const cos = items.filter(m => !m.is_offline && m.cos_phi !== null).map(m => m.cos_phi as number);
  const kvar = items.filter(m => !m.is_offline && m.kvar !== null).map(m => m.kvar as number);
  if (cos.length === 0 && kvar.length === 0) return [{ severity: "info", text: t("analysis.no_data") }];
  const out: AnalysisItem[] = [];
  if (cos.length > 0) {
    const a = avg(cos);
    if (a >= 0.95) {
      out.push({ severity: "ok", text: t("analysis.cos_ok", { avg: a.toFixed(2) }) });
    } else if (a >= 0.85) {
      out.push({ severity: "warn", text: t("analysis.cos_warn", { avg: a.toFixed(2) }) });
    } else {
      out.push({ severity: "bad", text: t("analysis.cos_bad", { avg: a.toFixed(2) }) });
    }
  }
  if (kvar.length > 0) {
    out.push({ severity: "info", text: t("analysis.kvar_avg", {
      avg: Math.round(avg(kvar)), min: Math.round(Math.min(...kvar)), max: Math.round(Math.max(...kvar)),
    }) });
  }
  return out;
}

export function analysePressure(items: Measurement[], t: T): AnalysisItem[] {
  const fields: { key: keyof Measurement; label: string }[] = [
    { key: "bp_371", label: "BP 371" },
    { key: "pn_351", label: "PN 351" },
    { key: "pn_352", label: "PN 352" },
  ];
  const out: AnalysisItem[] = [];
  let totalPoints = 0;
  for (const f of fields) {
    const vals = items.filter(m => !m.is_offline && (m[f.key] as number | null) !== null)
      .map(m => m[f.key] as unknown as number);
    if (vals.length === 0) continue;
    totalPoints += vals.length;
    const mn = Math.min(...vals), mx = Math.max(...vals);
    const range = mx - mn;
    const mean = avg(vals);
    // Treat a swing > 50% of mean as worth flagging (no fixed normative bands —
    // pressures are very turbine-specific).
    const sev: Severity = mean > 0 && range / mean > 0.5 ? "warn" : "info";
    out.push({ severity: sev, text: t("analysis.pressure_field", {
      name: f.label, avg: num(mean), min: num(mn), max: num(mx),
    }) });
  }
  if (totalPoints === 0) return [{ severity: "info", text: t("analysis.no_data") }];
  return out;
}

export function analyseScatter(items: Measurement[], t: T): AnalysisItem[] {
  const pts = items
    .filter(m => !m.is_offline && m.kw !== null && kvOf(m) !== null)
    .map(m => ({ kw: m.kw as number, kv: kvOf(m) as number }));
  if (pts.length < 3) return [{ severity: "info", text: t("analysis.scatter_few") }];

  const kws = pts.map(p => p.kw), kvs = pts.map(p => p.kv);
  const mKw = avg(kws), mKv = avg(kvs);
  const cov = pts.reduce((a, p) => a + (p.kw - mKw) * (p.kv - mKv), 0) / pts.length;
  const r = cov / (stddev(kws) * stddev(kvs) || 1);

  const out: AnalysisItem[] = [];
  out.push({ severity: "info", text: t("analysis.scatter_points", { n: pts.length }) });
  if (Math.abs(r) >= 0.5) {
    out.push({ severity: "ok", text: t(r > 0 ? "analysis.scatter_corr_pos" : "analysis.scatter_corr_neg", { r: r.toFixed(2) }) });
  } else {
    out.push({ severity: "info", text: t("analysis.scatter_corr_none", { r: r.toFixed(2) }) });
  }
  return out;
}
